import '../../styles/restScreens.css';
import { useCallback, useEffect, useState } from 'react';
import { supabaseClient } from '../../config/supabase';
import PhoneInput from '../ui/PhoneInput';
import { MAX_RECIPIENTS, emptyRecipient, recipientName, toRecipientPayload, validateRecipients } from '../../lib/clientRecipients';

import { BTN } from '../ui/platformStandard';
// BL-407 round 1b: what the client has done in the client app's first-run setup, shown on the contact card.
// Staff see card names and last 4 digits only, and can edit "Who gets what" and rename a card.
const SWITCHES = [
    ['receives_confirmations', 'Confirmations'],
    ['receives_invoices', 'Invoices'],
    ['receives_receipts', 'Receipts'],
];

function formatDate(value) {
    return value ? new Date(value).toLocaleDateString() : '';
}

function cardName(card) {
    return `${card.nickname || card.brand || 'Card'} ending ${card.last4}`;
}

// Saves the list without ever leaving the client in a state the database refuses: a billing contact is cleared before a
// new one is set, switches are only turned on before any are turned off, and removed people are deleted last.
async function saveRecipients({ contact, original, draft }) {
    const payload = toRecipientPayload(draft);
    const rows = draft.map((person, index) => ({ id: person.id, row: { ...payload[index], sort_order: index }, person }));
    const table = () => supabaseClient.from('client_document_recipients');
    const run = async (request) => {
        const { error } = await request;
        if (error) throw error;
    };
    const originalById = new Map(original.map((person) => [person.id, person]));
    // The client's own record keeps its name and email; staff change only the switches.
    const editable = ({ row, person }) => {
        if (!person.is_self) return row;
        const {
            is_self: _self, is_billing_contact: _billing, first_name: _first, last_name: _last, email: _email, phone: _phone, ...rest
        } = row;
        return rest;
    };
    for (const { id, row } of rows) {
        const before = originalById.get(id);
        if (before?.is_billing_contact && !row.is_billing_contact) await run(table().update({ is_billing_contact: false }).eq('id', id));
    }
    for (const entry of rows) {
        const before = originalById.get(entry.id);
        if (before) {
            const grown = {
                ...editable(entry),
                receives_confirmations: before.receives_confirmations || entry.row.receives_confirmations,
                receives_invoices: before.receives_invoices || entry.row.receives_invoices,
                receives_receipts: before.receives_receipts || entry.row.receives_receipts,
            };
            await run(table().update(grown).eq('id', entry.id));
        } else {
            await run(table().insert({ ...entry.row, org_id: contact.org_id, contact_id: contact.id }));
        }
    }
    for (const entry of rows) {
        if (originalById.has(entry.id)) await run(table().update(editable(entry)).eq('id', entry.id));
    }
    const keep = new Set(rows.map(({ id }) => id).filter(Boolean));
    for (const person of original) {
        if (!keep.has(person.id)) await run(table().delete().eq('id', person.id));
    }
}

export default function ClientSetupPanel({ contact }) {
    const [summary, setSummary] = useState(null);
    const [loading, setLoading] = useState(true);
    const [isAdmin, setIsAdmin] = useState(false);
    const [message, setMessage] = useState('');
    const [problem, setProblem] = useState('');
    const [draft, setDraft] = useState(null);
    const [saving, setSaving] = useState(false);
    const [renaming, setRenaming] = useState(null);
    const [confirmRestart, setConfirmRestart] = useState(false);

    const load = useCallback(async () => {
        try {
            const { data, error } = await supabaseClient.rpc('bl407_staff_client_setup_summary', { p_contact: contact.id });
            if (error) throw error;
            setSummary(data || null);
        } catch (failure) {
            setProblem(`Client setup could not be loaded: ${failure.message}`);
        } finally {
            setLoading(false);
        }
    }, [contact.id]);

    useEffect(() => { load(); }, [load]);
    useEffect(() => {
        let active = true;
        Promise.resolve().then(() => supabaseClient.rpc('is_org_admin'))
            .then(({ data }) => { if (active) setIsAdmin(data === true); })
            .catch(() => { if (active) setIsAdmin(false); });
        return () => { active = false; };
    }, []);

    if (loading) return <p data-m6-layout="clients" data-m6-component="ClientSetupPanel" className="mt-5 text-xs text-[var(--ink-muted)]">Loading client setup…</p>;
    if (!summary) {
        return problem ? <p className="mt-5 text-xs text-red-700" role="alert">{problem}</p> : null;
    }

    const { terms, cards, recipients } = summary;
    const billing = recipients.find((person) => person.is_billing_contact);

    const updateDraft = (index, patch) => {
        setDraft((list) => list.map((person, at) => (at === index ? { ...person, ...patch } : person)));
        setMessage('');
    };
    const setBilling = (index, on) => setDraft((list) => list.map((person, at) => ({
        ...person, is_billing_contact: at === index ? on : (on ? false : person.is_billing_contact),
    })));

    async function save() {
        const check = validateRecipients(draft);
        if (!check.ok) { setProblem(check.error); return; }
        setSaving(true);
        setProblem('');
        try {
            await saveRecipients({ contact, original: recipients, draft });
            setDraft(null);
            setMessage('Who gets what was saved.');
            await load();
        } catch (failure) {
            setProblem(`Who gets what could not be saved: ${failure.message}`);
            await load();
        } finally { setSaving(false); }
    }

    async function saveNickname() {
        setSaving(true);
        setProblem('');
        try {
            const { error } = await supabaseClient.rpc('bl407_set_card_nickname', { p_card: renaming.id, p_nickname: renaming.value });
            if (error) throw error;
            setRenaming(null);
            setMessage('Card name saved.');
            await load();
        } catch (failure) {
            setProblem(`The card name could not be saved: ${failure.message}`);
        } finally { setSaving(false); }
    }

    async function restart() {
        setSaving(true);
        setProblem('');
        try {
            const { data, error } = await supabaseClient.rpc('bl407_staff_restart_client_setup', { p_contact: contact.id });
            if (error) throw error;
            setConfirmRestart(false);
            setMessage(`Setup restarted (${data ?? 0} steps reset). The client starts again the next time they open the app.`);
            await load();
        } catch (failure) {
            setProblem(`Setup could not be restarted: ${failure.message}`);
        } finally { setSaving(false); }
    }

    const people = draft || recipients;
    return (
        <section data-m6-layout="clients" data-m6-component="ClientSetupPanel" className="mt-5 rounded-lg border border-[var(--surface-border)] p-4" aria-label="Client setup">
            <h3 className="text-sm font-semibold text-[var(--ink)]">Client setup</h3>
            <p className="mt-1 text-sm text-[var(--ink)]">Setup: {summary.completed_count} of {summary.total} steps</p>
            <ul className="mt-1 text-xs text-[var(--ink-muted)]">
                {summary.steps.map((step) => (
                    <li key={step.key}>{step.status === 'completed' ? 'Done' : 'Not done'} · {step.label}{step.completed_at ? ` (${formatDate(step.completed_at)})` : ''}</li>
                ))}
            </ul>

            <h4 className="mt-3 text-xs font-medium text-[var(--ink)]">Service terms</h4>
            <p className="text-xs text-[var(--ink-muted)]">
                {terms?.accepted_version
                    ? `Service terms: accepted version ${terms.accepted_version} on ${formatDate(terms.accepted_at)}`
                    : 'Service terms: not accepted yet'}
            </p>
            {terms?.current_version && terms.current === false && (
                <p className="text-xs text-amber-700">Newer terms are waiting to be accepted.</p>
            )}

            <h4 className="mt-3 text-xs font-medium text-[var(--ink)]">Cards on file</h4>
            {cards.length === 0 ? <p className="text-xs text-[var(--ink-muted)]">No cards on file.</p> : (
                <ul className="text-xs text-[var(--ink-muted)]">
                    {cards.map((card) => (
                        <li key={card.id} className="py-0.5">
                            {renaming?.id === card.id ? (
                                <span className="inline-flex items-center gap-2">
                                    <label htmlFor={`card-name-${card.id}`} className="sr-only">Card name</label>
                                    <input id={`card-name-${card.id}`} type="text" maxLength={30} value={renaming.value}
                                        onChange={(event) => setRenaming({ ...renaming, value: event.target.value })} />
                                    <button type="button" className={BTN} disabled={saving} onClick={saveNickname}>Save name</button>
                                    <button type="button" className={BTN} onClick={() => setRenaming(null)}>Cancel</button>
                                </span>
                            ) : (
                                <>
                                    {cardName(card)}{card.is_default ? ' (main card)' : ''}{' '}
                                    <button type="button" className="underline" onClick={() => setRenaming({ id: card.id, value: card.nickname || '' })}>Rename</button>
                                </>
                            )}
                        </li>
                    ))}
                </ul>
            )}

            <h4 className="mt-3 text-xs font-medium text-[var(--ink)]">Who gets what</h4>
            <p className="text-xs text-[var(--ink-muted)]">
                Billing contact: {billing ? `${recipientName(billing)} (${billing.email})` : 'none, the client handles their own billing'}
            </p>
            {people.length === 0 && <p className="text-xs text-[var(--ink-muted)]">The client has not chosen yet.</p>}
            <ul className="mt-1 space-y-2 text-xs text-[var(--ink-muted)]">
                {people.map((person, index) => (
                    <li key={person.id || `new-${index}`} className="rounded border border-[var(--surface-border)] p-2">
                        {draft ? (
                            <div className="space-y-1">
                                {person.is_self ? <p className="text-[var(--ink)]">{recipientName(person)} (the client) · {person.email}</p> : (
                                    <div className="flex flex-wrap gap-2">
                                        <input aria-label="First name" placeholder="First name" value={person.first_name} onChange={(event) => updateDraft(index, { first_name: event.target.value })} />
                                        <input aria-label="Last name" placeholder="Last name" value={person.last_name} onChange={(event) => updateDraft(index, { last_name: event.target.value })} />
                                        <input aria-label="Email" placeholder="Email" value={person.email} onChange={(event) => updateDraft(index, { email: event.target.value })} />
                                        <PhoneInput aria-label="Phone" placeholder="Phone (optional)" value={person.phone || ''} onChange={(phone) => updateDraft(index, { phone })} />
                                    </div>
                                )}
                                <div className="flex flex-wrap gap-3">
                                    {SWITCHES.map(([field, label]) => (
                                        <label key={field} className="inline-flex items-center gap-1">
                                            <input type="checkbox" role="switch" aria-label={`${label} for ${recipientName(person) || 'this person'}`}
                                                checked={Boolean(person[field])} onChange={(event) => updateDraft(index, { [field]: event.target.checked })} />
                                            {label}
                                        </label>
                                    ))}
                                    <label className="inline-flex items-center gap-1">
                                        <input type="checkbox" checked={Boolean(person.include_pricing)}
                                            onChange={(event) => updateDraft(index, { include_pricing: event.target.checked })} />
                                        Include fares, fees and payment details
                                    </label>
                                    {!person.is_self && (
                                        <label className="inline-flex items-center gap-1">
                                            <input type="checkbox" checked={Boolean(person.is_billing_contact)} onChange={(event) => setBilling(index, event.target.checked)} />
                                            Billing contact
                                        </label>
                                    )}
                                    {!person.is_self && (
                                        <button type="button" className="underline" onClick={() => setDraft((list) => list.filter((_, at) => at !== index))}>
                                            Remove {recipientName(person) || 'this person'}
                                        </button>
                                    )}
                                </div>
                            </div>
                        ) : (
                            <>
                                <span className="text-[var(--ink)]">{recipientName(person)}{person.is_self ? ' (the client)' : ''}</span> · {person.email}
                                <br />
                                Gets: {SWITCHES.filter(([field]) => person[field]).map(([, label]) => label).join(', ') || 'nothing'}
                                {person.include_pricing ? ' · with fares, fees and payment details' : ' · without prices'}
                            </>
                        )}
                    </li>
                ))}
            </ul>
            {draft ? (
                <div className="mt-2 flex flex-wrap gap-2">
                    <button type="button" className={BTN} disabled={draft.length >= MAX_RECIPIENTS}
                        onClick={() => setDraft((list) => [...list, emptyRecipient()])}>Add another person</button>
                    <button type="button" className={BTN} disabled={saving} onClick={save}>Save who gets what</button>
                    <button type="button" className={BTN} onClick={() => { setDraft(null); setProblem(''); }}>Cancel</button>
                </div>
            ) : recipients.length > 0 && (
                <button type="button" className={`${BTN} mt-2`}
                    onClick={() => { setDraft(recipients.map((person) => ({ ...person }))); setMessage(''); }}>Edit who gets what</button>
            )}

            {isAdmin && (
                <div className="mt-4 border-t border-[var(--surface-border)] pt-3">
                    {confirmRestart ? (
                        <div>
                            <p className="text-xs text-[var(--ink)]">This sends the client back to the start of setup the next time they open the app. Their saved cards, text-consent history and accepted terms are kept.</p>
                            <div className="mt-2 flex gap-2">
                                <button type="button" className={BTN} disabled={saving} onClick={restart}>Yes, restart setup</button>
                                <button type="button" className={BTN} onClick={() => setConfirmRestart(false)}>Cancel</button>
                            </div>
                        </div>
                    ) : (
                        <button type="button" className={BTN} onClick={() => { setConfirmRestart(true); setMessage(''); }}>Restart client setup</button>
                    )}
                </div>
            )}
            {problem && <p className="mt-2 text-xs text-red-700" role="alert">{problem}</p>}
            {message && <p className="mt-2 text-xs text-[var(--ink)]" role="status">{message}</p>}
        </section>
    );
}
